import React, { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '~/components/Button';
import { useConfirm, useMessage } from '~/components/Dialogs';
import { IModalComponent, useModals } from '~/components/Modal';
import { Table } from '~/components/Table';
import { Title } from '~/components/Title';
import { downloadCsv } from '~/lib/file';
import { Intent } from '~/lib/intent';
import { ID } from '~/lib/model';
import { importPlacements } from '~/modules/Sources/api';
import { IImportResult } from '~/modules/Sources/models/Placement';
import style from '~/modules/Sources/components/index.module.scss';

interface IImportPlacementsButtonProps {
    sellerId?: ID;
    onImported?: () => void;
}

const errorText = (e: any): string =>
    e?.response?.data?.message || (typeof e?.response?.data === 'string' && e.response.data) || e?.message || 'Import failed';

interface IImportModalProps {
    sellerId?: ID;
    onDone: (result: IImportResult, fileName: string) => void;
}

const ImportModal: IModalComponent<IImportModalProps> = ({ sellerId, onDone, close }) => {
    const message = useMessage();
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);

    const mutation = useMutation({
        mutationFn: (f: File) => importPlacements(f, sellerId),
        onSuccess: (result: IImportResult, f: File) => {
            close?.();
            onDone(result, f.name);
        },
        onError: (e: any) => message(errorText(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> }),
    });

    return (
        <div>
            <Title>Import placements from MAX</Title>
            <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className={style.fileInput}
                onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    e.target.value = '';
                }}
            />
            <div className={style.dropZone} onClick={() => inputRef.current?.click()}>
                {file ? <strong>{file.name}</strong> : 'Choose the MAX Ad Unit Manager export (.csv)'}
            </div>
            <p><strong>What happens</strong></p>
            <ul>
                <li>Only <code>BidMachine Bidding</code> rows for your sources are read. Other networks and other accounts' sources are skipped.</li>
                <li>Each ad unit becomes a placement with its MAX Ad Unit ID. Existing placements are updated, never deleted, and their GPIDs are left as they are.</li>
                <li>You get the same file back with our placement id written into <code>Ad Unit ID / Placement ID</code>. Upload it to MAX to finish.</li>
            </ul>
            <div className={style.actions}>
                <Button type="button" onClick={close}>Cancel</Button>
                <Button
                    type="button"
                    intent={Intent.PRIMARY}
                    disabled={!file || mutation.isPending}
                    onClick={() => file && mutation.mutate(file)}
                >
                    {mutation.isPending ? 'Importing…' : 'Import'}
                </Button>
            </div>
        </div>
    );
};

interface IResultModalProps {
    result: IImportResult;
    fileName: string;
}

const ResultModal: IModalComponent<IResultModalProps> = ({ result, fileName, close }) => {
    const confirm = useConfirm();
    const [downloaded, setDownloaded] = useState(false);
    const unmatched = result.skipped - result.bySource.reduce((sum, s) => sum + s.skipped, 0);

    const download = () => {
        downloadCsv(`${fileName.replace(/\.csv$/i, '')}-bidmachine.csv`, result.csv);
        setDownloaded(true);
    };

    const onClose = () =>
        downloaded
            ? close?.()
            : confirm('Download the CSV for MAX before closing? Ok downloads it, Cancel closes without it.', {
                  intent: Intent.WARNING,
                  icon: <i className="fa fa-question" />,
                  onOk: () => {
                      download();
                      close?.();
                  },
                  onNo: () => close?.(),
              });

    return (
        <div>
            <Title>Import finished</Title>
            <p>
                Created <strong>{result.created}</strong>, updated <strong>{result.updated}</strong>, skipped <strong>{result.skipped}</strong>
            </p>
            <Table
                header
                records={result.bySource}
                columns={[
                    { header: 'Source', field: 'sourceId' },
                    { header: 'Created', field: 'created' },
                    { header: 'Updated', field: 'updated' },
                    { header: 'Skipped', field: 'skipped' },
                ]}
            />
            {unmatched > 0 && (
                <p>
                    <strong>{unmatched}</strong> {unmatched === 1 ? 'row' : 'rows'} skipped before a source matched (source not in this account or a short row).
                </p>
            )}
            {result.skippedRows.length > 0 && (
                <>
                    <p><strong>Skipped rows</strong></p>
                    <Table
                        header
                        records={result.skippedRows}
                        columns={[
                            { header: 'Row', field: 'row', small: true },
                            { header: 'Ad unit', field: 'adUnitName', cellWrap: 'break-word' },
                            { header: 'Reason', field: 'reason', cellWrap: 'break-word' },
                        ]}
                    />
                </>
            )}
            <p>Closed this by mistake? Upload the same export again: nothing changes and you get the same file back.</p>
            <div className={style.actions}>
                <Button type="button" onClick={onClose}>Close</Button>
                <Button type="button" intent={Intent.PRIMARY} onClick={download}>Download CSV for MAX</Button>
            </div>
        </div>
    );
};

export const ImportPlacementsButton = ({ sellerId, onImported }: IImportPlacementsButtonProps) => {
    const modals = useModals();
    const resultModal = modals.create(ResultModal);
    const importModal = modals.create(ImportModal, {
        sellerId,
        onDone: (result, fileName) => {
            onImported?.();
            resultModal.show({ result, fileName });
        },
    });

    return (
        <Button type="button" leftIcon={<i className="fa fa-upload" />} onClick={() => importModal.show()}>
            Import from MAX CSV
        </Button>
    );
};
